import React, { useEffect, useState } from 'react';
import { useEnv } from 'src/context/EnvProvider';
import { useTexture } from 'src/hook/useTexture';
import { getPiece } from 'src/utils/configurator.utils';
import { getIndexType } from 'src/utils/3d.utils';
import { selectedTexture } from './utils';
import { TexturesProps, SortType } from './types';
import {
  useActiveGranits,
  useFilteredGranits,
  useSortedGranits,
} from './hooks';
import { useTextureStock } from './useTextureStock';
import { useChangeGranit } from './useChangeGranit';
import { Toolbar } from './Toolbar';
import { TextureSelection } from './TextureSelection';
import { StockModal } from './StockModal';
import { AdvancedMode } from 'src/components/3d/Fragments/AdvancedMode';
import { StateNames } from 'src/Screens/Configurator';
import { GranitGrid } from './GranitGrid';

export const Textures = ({
  activePieces,
  setActivePieces,
  pieceIndex,
  customSteles,
  setCustomSteles,
  customPlatings,
  setCustomPlatings,
  setPieceIndex,
  getValue,
  pieces,
  setValue,
  customSoles,
  setCustomSoles,
  customBoxes,
  setCustomBoxes,
  history,
}: TexturesProps) => {
  const { buckets } = useEnv();
  const { granits } = useTexture();
  const [selectedDominant, setSelectedDominant] = useState<string>();
  const [hasSelectedAllPieces, setHasSelectedAllPieces] =
    useState<boolean>(false);
  const [filter, setFilter] = useState<string>('');
  const [error, setError] = useState<string>();
  const [sort, setSort] = useState<SortType>('ascending_name');
  const [origin, setOrigin] = React.useState<string[]>([]);
  const [seeAssemblyStock, setSeeAssemblyStock] = useState<boolean>(false);
  const [isStockModalOpen, setIsStockModalOpen] = useState<boolean>(false);

  const type = getIndexType(
    pieceIndex,
    activePieces,
    customSteles,
    customPlatings,
    customSoles,
    customBoxes,
  );

  const piece = getPiece(
    pieceIndex,
    pieces,
    customSteles,
    customPlatings,
    customSoles,
    customBoxes,
  );

  // Sync selectedDominant when the current piece's granit changes
  useEffect(() => {
    if (
      selectedDominant &&
      activePieces[pieceIndex]?.granit?.id !== selectedDominant
    ) {
      setSelectedDominant(activePieces[pieceIndex]?.granit?.id);
    }
  }, [selectedDominant, activePieces, pieceIndex]);

  // --- Data fetching ---
  const {
    stockData,
    stockStelesData,
    assemblyStockData,
    refreshAssemblyStock,
  } = useTextureStock(piece, pieces, type, history);

  // --- Granit lists ---
  const activeGranits = useActiveGranits(
    activePieces,
    customSteles,
    customPlatings,
    customSoles,
    customBoxes,
  );

  const filteredGranits = useFilteredGranits(
    granits,
    pieceIndex,
    activePieces,
    filter,
    origin,
    seeAssemblyStock,
    assemblyStockData ?? [],
  );

  const sortedGranits = useSortedGranits(filteredGranits, sort);

  // --- Handlers ---
  const {
    handleChangeGranit,
    handleSelectPieceWithGranitId,
    handleApplyStock,
  } = useChangeGranit({
    pieceIndex,
    activePieces,
    customSteles,
    customPlatings,
    customSoles,
    customBoxes,
    pieces,
    granits,
    buckets,
    hasAdvancedMode: getValue('hasAdvancedMode'),
    selectedDominant,
    hasSelectedAllPieces,
    setActivePieces,
    setCustomSteles,
    setCustomPlatings,
    setCustomSoles,
    setCustomBoxes,
    setError,
    setSelectedDominant,
    setPieceIndex,
    setIsStockModalOpen,
  });

  const currentSelectedTexture = selectedTexture(
    pieceIndex,
    activePieces,
    customSteles,
    customPlatings,
    customSoles,
    customBoxes,
  );

  const bucketUrl = buckets?.BUCKET_3D_PIECES || '';

  return (
    <div className='flex flex-col h-full'>
      <Toolbar
        filter={filter}
        setFilter={setFilter}
        sort={sort}
        setSort={setSort}
        origin={origin}
        setOrigin={setOrigin}
        onOpenStock={() => {
          refreshAssemblyStock();
          setIsStockModalOpen(true);
        }}
      />

      {/* Grille de granits */}
      <div
        className='flex-1 overflow-y-auto p-5'
        style={{ maxHeight: 'calc(100vh - 400px)' }}
      >
        {/* Header Section */}
        <div className='flex flex-col mb-4'>
          <div className='flex flex-row justify-end items-center w-full'>
            <AdvancedMode getValue={getValue} setValue={setValue} />
          </div>

          {error && (
            <div className='mt-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg'>
              <p className='text-xs font-medium text-red-600'>{'error'}</p>
            </div>
          )}
        </div>

        {/* Sélection de pièces */}
        <div className='flex flex-wrap gap-2 items-center mb-4'>
          <TextureSelection
            hasSelectedAllPieces={hasSelectedAllPieces}
            setHasSelectedAllPieces={setHasSelectedAllPieces}
            activeGranits={activeGranits}
            selectedDominant={selectedDominant}
            setSelectedDominant={setSelectedDominant}
            buckets={buckets ? buckets : { BUCKET_3D_PIECES: '' }}
            activePieces={activePieces}
            pieceIndex={pieceIndex}
            setValue={(stateName: string, value: any) =>
              setValue(stateName as StateNames, value)
            }
            selectPieceWithGranitId={handleSelectPieceWithGranitId}
          />
        </div>

        <GranitGrid
          sortedGranits={sortedGranits}
          type={type}
          piece={piece}
          stockData={stockData}
          stockStelesData={stockStelesData}
          seeAssemblyStock={seeAssemblyStock}
          currentSelectedTexture={currentSelectedTexture}
          bucketUrl={bucketUrl}
          onChangeGranit={handleChangeGranit}
        />
      </div>

      <StockModal
        isOpen={isStockModalOpen}
        onClose={() => setIsStockModalOpen(false)}
        stockData={assemblyStockData ?? null}
        onApplyStock={handleApplyStock}
        bucketUrl={bucketUrl}
      />
    </div>
  );
};
